import { AxiosError } from 'axios'
import { useCallJobClaimData } from 'features/calls/callsHooks'
import { CallTypeToJobTypeEnum, ICall, UseCaseEnumToUseCaseIdEnum } from 'features/calls/types'
import { ProviderRecord } from 'features/claims/components/ClaimDetails/ProviderRecord/ProviderRecord'
import { useAppealEnumDescriptions } from 'features/claims/hooks/useAppealEnumDescriptions'
import { IClaim } from 'features/claims/types'
import { FaInfoCircle, FaDownload } from 'react-icons/fa'
import { Button, Card, Tooltip } from '@outbound-ai/ursa'

import styled from 'styled-components'
import { PositionEnum } from 'types/cssTypes'
import { ErrorMessagesEnum } from 'types/errorTypes'
import { dateDiffToHHMMSS, formatAMPM, formatDateOnly, formatDateTime } from 'utils/date'
import { errorHandler } from 'utils/errors/errors'
import { isCurrentUserOutboundUser } from 'utils/helper'
import { apiPaths } from 'configs/apiConfigs'
import { httpClaims } from 'utils/http'
import { notificator } from 'utils/notificator/notificator'

import { getCallDetails } from 'features/calls/callsApi'
import { resolveExtractedStatusDisplay } from 'features/claims/claimsApi'
import { useClaimStatuses, useDetailedClaimStatuses } from 'features/claims/claimsHooks'
import { useEntities } from 'features/entities/entityHooks'
import { useEffect, useState } from 'react'
import { formatAppealStatusDescription, mapEntities } from 'utils/dataExtractor'
import { PostCallEntities } from './PostCallEntities'
import { SummaryCard } from 'components/Display/SummaryCard'

interface IProps {
  call: ICall
  claim: IClaim
}

export const PostCallExtractedEntities = ({ call, claim }: IProps) => {
  const [slotData, setSlotData] = useState<any[]>([])
  const [callDetailsData, setCallDetailsData] = useState<any[]>([])
  const [slotExtracted, setSlotExtracted] = useState<any[]>([])

  const currentUserOUtboundUser = isCurrentUserOutboundUser()

  const isCallJobClaimDataAvailable = call?.api_ClaimData_Id ? true : false
  const callJobClaimData = isCallJobClaimDataAvailable ? useCallJobClaimData(call.jobId)?.data : []
  const { data: entities } = useEntities({
    onError: (error: AxiosError) => errorHandler.handleError(error, ErrorMessagesEnum.EntitiesError),
  })
  const { options: claimStatuses } = useClaimStatuses()
  const { options: detailedClaimStatuses } = useDetailedClaimStatuses()
  const appealEnumDescriptions = useAppealEnumDescriptions()

  useEffect(() => {
    if (entities && claim) {
      const mappedSlotData = mapEntities((entities as any) || [], claim || {})
      setSlotData(mappedSlotData)
      getCallDetails(claim.claimId, call.jbId).then((res) => {
        setCallDetailsData(res as any)
        const mappedExtractedSlots = mapEntities((entities as any) || [], res || {})
        setSlotExtracted(mappedExtractedSlots)
      })
    }
  }, [entities, claim])

  const callDetails = [
    {
      label: 'Call Initiator',
      value: call.initiatorUser ? call.initiatorUser.firstName + ' ' + call.initiatorUser.lastName : '-',
    },
    {
      label: 'Date Conducted',
      value: formatDateTime(call.jobStarted) || '-',
    },
    {
      label: 'Call  Time',
      value: `${formatAMPM(call.jobStarted)} - ${formatAMPM(call.jobCompleted)}`,
    },
    {
      label: 'Call  Duration',
      value: dateDiffToHHMMSS(call.jobCompleted, call.jobStarted) || '-',
    },
    {
      label: 'Call  Reference',
      value: slotExtracted.find((el) => el.slot === 'call_reference_payer_ivr')?.value || '---',
      infoText:
        'Some payers provide a full reference number while others only provide the last four digits. Check with payer rep to confirm.',
    },
    {
      label: 'Claim Count',
      value: slotExtracted.find((el) => el.slot === 'claim_count_found')?.value || '---',
      onlyInternal: true,
    },
  ]

  /*   const compoareBillAmount =
    Number(getEntityVersionFromClaim(WellKnownEntitiesEnum.claimAmount, callEntities)?.entityValue) ==
    Number(
      extractValues([
        WellKnownEntitiesEnum.payor_billed_amount_payer_ivr,
        WellKnownEntitiesEnum.payor_billed_amount_payer_agent,
      ]),
    ) */

  const formatValue = (item: any) => {
    if (!item?.value || item.value === '---') return ''
    if (item.typeId === 'dateonly') return formatDateOnly(item.value)
    if (item.slot === 'appeal_status') {
      const description = appealEnumDescriptions.appeal_status?.[item.value] ?? item.value
      return formatAppealStatusDescription(description)
    }
    return resolveExtractedStatusDisplay(item.slot, item.value, claimStatuses, detailedClaimStatuses, {
      claimStatusCode: call.claimStatusCode,
      detailedClaimStatusCode: call.detailedClaimStatusCode,
    })
  }

  const deriveExtractedSlotPriority = (slots: (string | string[])[]) =>
    slots.reduce((result: any[], item) => {
      const slotNames = typeof item === 'string' ? [item] : item
      const firstSlot = slotExtracted.find((el) => el.slot === slotNames[0])
      if (!firstSlot) return result

      const selected =
        slotNames
          .map((name) => slotExtracted.find((el) => el.slot === name))
          .find((el) => el?.value && el.value !== '---') || firstSlot

      result.push({
        label: firstSlot.description,
        name: firstSlot.slot,
        value: formatValue(selected),
        currency: selected.typeId === 'decimal',
      })

      return result
    }, [])

  const deriveExtractedSlots = (slots: string[]) => {
    const dataFiltered = slotExtracted.filter((el) => slots.includes(el.slot))
    const finalData = dataFiltered.map((item) => ({
      label: item.description,
      name: item.slot,
      value: formatValue(item),
      currency: item.typeId === 'decimal' ? true : false,
    }))
    return finalData
  }

  const extractedSlotIdentification = ['insurer_claim_id_payer_ivr', 'billed_amount_payer_ivr']

  const extractIdentification = deriveExtractedSlots(extractedSlotIdentification)

  const extractClaimProcessingSlots = [
    'claim_status_payer',
    'coverage_order',
    'claim_received_date_payer_ivr',
    'claim_processed_date_payer_ivr',
    'detailed_claim_status',
  ]

  const extractClaimProcessing = deriveExtractedSlots(extractClaimProcessingSlots)

  const extractChargesSlots = ['discount_amount_payer_ivr', 'total_covered_payer_ivr', 'total_not_covered_payer_ivr']

  const extractCharges = deriveExtractedSlots(extractChargesSlots)

  const extractPaymentSlots = [
    'payment_method_payer_ivr',
    'plan_paid_amount_payer_ivr',
    [
      'payment_date_payer_ivr',
      'ach_payment_date_payer_ivr',
      'check_payment_date_payer_ivr',
      'eft_payment_date_payer_ivr',
    ],
    'payment_posted_date_payer_ivr',
    [
      'payment_reference_payer_ivr',
      'ach_payment_reference_payer_ivr',
      'check_payment_reference_payer_ivr',
      'eft_payment_reference_payer_ivr',
    ],
    'provider_responsibility_amount_payer_ivr',
    'remittance_advice_date_payer_ivr',
    'hh2h_callback_number_payer_agent',
    'fax_number_global_payer_agent',
  ]

  const extractPayment = deriveExtractedSlotPriority(extractPaymentSlots)

  const extractAppealStatusInformationSlots = deriveExtractedSlots([
    'appeal_status',
    'appeal_case_number',
    'appeal_receipt_date',
    'appeal_decision_date',
    'appeal_status_call_summary',
    'confirmed_claim_partially_paid',
    'confirmed_no_additional_appeal_payment',
    'confirmed_next_level_appeal_available',
    'confirmed_final_level_appeal',
    'appeal_determination_date',
    'appeal_determination_letter_date',
    'appeal_document_number_asc',
  ])

  const extractPaitentResponsibilitySlots = [
    'coinsurance_payment_amount_payer_ivr',
    'applied_to_deductible_amount_payer_ivr',
    'patient_responsibile_amount_payer_ivr',
  ]

  const extractPaitentResponsibility = deriveExtractedSlots(extractPaitentResponsibilitySlots)

  const otherExtractions = ['tpa_name', 'tpa_phone_number']
  const extractOtherExtractions = deriveExtractedSlots(otherExtractions)

  const arr = Array.isArray(callDetailsData) ? callDetailsData : [callDetailsData]

  const extractedGenCallSummary = arr.map((item) => item.extraction?.callSummary ?? null)

  /* const extractDeductiblesSlots = [
    'paid_towards_in_network_individual_deductible_payer_ivr',
    'in_network_individual_deductible_payer_ivr',
    'paid_towards_in_network_family_deductible_payer_ivr',
    'in_network_family_deductible_payer_ivr',
  ]

  const extractDeductibles = deriveExtractedSlots(extractDeductiblesSlots)

  const extractOutOfPocketSlots = [
    'paid_towards_individual_in_network_maximum_payer_ivr',
    'individual_in_network_maximum_payer_ivr',
    'paid_towards_family_in_network_maximum_payer_ivr',
    'family_in_network_maximum_payer_ivr',
  ]

  const extractOutOfPocket = deriveExtractedSlots(extractOutOfPocketSlots)

  const extractCoPaySlots = [
    'in_network_primary_care_physician_copay_payer_ivr',
    'in_network_specialist_copay_payer_ivr',
    'in_network_urgent_care_copay_payer_ivr',
    'in_network_emergency_room_copay_payer_ivr',
    'in_network_behavioral_outpatient_service_copay_payer_ivr',
  ]

  const extractCoPay = deriveExtractedSlots(extractCoPaySlots)

  const extractCoInsuranceSlots = ['in_network_coinsurance_max_payer_ivr', 'in_network_coinsurance_min_payer_ivr']

  const extractCoInsurance = deriveExtractedSlots(extractCoInsuranceSlots)

  const extractDeductiblesOutSlots = [
    'paid_towards_out_network_individual_deductible_payer_ivr',
    'out_network_individual_deductible_payer_ivr',
    'paid_towards_out_network_family_deductible_payer_ivr',
    'out_network_family_deductible_payer_ivr',
  ]

  const extractDeductiblesOut = deriveExtractedSlots(extractDeductiblesOutSlots)

  const extractOutOfPocketOutSlots = [
    'paid_towrads_individual_out_network_maximum_payer_ivr',
    'individual_out_network_maximum_payer_ivr',
    'paid_towrads_family_out_network_maximum_payer_ivr',
    'family_out_network_maximum_payer_ivr',
  ]

  const extractOutOfPocketOut = deriveExtractedSlots(extractOutOfPocketOutSlots)

  const extractCoPayOutSlots = [
    'out_network_primary_care_physician_copay_payer_ivr',
    'out_network_specialist_copay_payer_ivr',
    'out_network_urgent_care_copay_payer_ivr',
    'out_network_emergency_room_copay_payer_ivr',
    'out_network_behavioral_outpatient_service_copay_payer_ivr',
  ]

  const extractCoPayOut = deriveExtractedSlots(extractCoPayOutSlots)

  const extractCoInsuranceOutSlots = ['out_network_coinsurance_max_payer_ivr', 'out_network_coinsurance_min_payer_ivr']

  const extractCoInsuranceOut = deriveExtractedSlots(extractCoInsuranceOutSlots) */

  const handleDownloadJSON = async (apiPath: string, filename: string, successMessage: string) => {
    try {
      const response = await httpClaims.get(apiPath)
      const jsonString = JSON.stringify(response.data, null, 2)
      const blob = new Blob([jsonString], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      notificator.notify({
        heading: successMessage,
        type: 'success',
      })
    } catch (error) {
      errorHandler.handleError(error as AxiosError, ErrorMessagesEnum.Unknown)
    }
  }

  const handleDownloadAPIResponse = async () => {
    await handleDownloadJSON(
      apiPaths.callJobAPIResource(call.jbId),
      `uhc-api-response-${call.jbId}.json`,
      'API Response downloaded successfully',
    )
  }

  const handelDownloadExtractor = async () => {
    await handleDownloadJSON(
      apiPaths.callJobExtractionResource(call.jbId, 'DataExtractor_IVR'),
      `extraction-response-${call.jbId}.json`,
      'Call Extraction Response downloaded successfully',
    )
  }

  const handleDownloadAppealExtractor = async () => {
    await handleDownloadJSON(
      apiPaths.callJobExtractionResource(call.jbId, 'hca_slots_json'),
      `extraction-hca-response-${call.jbId}.json`,
      'Call Extraction HCA Response downloaded successfully',
    )
  }

  return (
    <Div>
      <div className="provider-wrapper">
        <ProviderRecord call={call} slotData={slotData} />
      </div>

      <Card title="Call Details" className="cards">
        <div className="call-details">
          {callDetails.map((detail) =>
            detail.onlyInternal && !currentUserOUtboundUser ? null : (
              <div className="call-detail" key={detail.label}>
                <div className="call-detail-label">
                  <span>
                    {detail.label}{' '}
                    {detail.infoText ? (
                      <Tooltip
                        className="text-with-tooltip"
                        tooltipText={detail.infoText}
                        position={PositionEnum.bottom}
                        positionContent={100}
                        positionTooltipArrow={85}
                        tooltipStyles={{
                          'max-width': '18rem',
                          width: '18rem',
                          transform: 'translateY(68%) translateX(-87%)',
                        }}
                        hideTimeout={0}
                      >
                        <FaInfoCircle />
                      </Tooltip>
                    ) : (
                      ''
                    )}
                  </span>
                </div>
                <div className="call-detail-value">{detail.value}</div>
              </div>
            ),
          )}
        </div>
      </Card>
      {currentUserOUtboundUser && isCallJobClaimDataAvailable && callJobClaimData && callJobClaimData?.length > 0 && (
        <Card title="Payer API Message" className="cards">
          <div className="container">
            {/* Header */}
            <div className="row bg-light font-weight-bold text-left py-2 border ">
              <div className="col-6">Error Message</div>
              <div className="col-2">API</div>
              <div className="col-4">Payer Id(s)</div>
            </div>
            {callJobClaimData?.map((callJob: any) => (
              <div key={callJob.label} className="row border py-2">
                <div className="col-6">{callJob.errorMessage}</div>
                <div className="col-2">{callJob.api}</div>
                <div className="col-4">{callJob.payerIds}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <SummaryCard
        title="Call Summary"
        action={extractedGenCallSummary ? true : false}
        data={extractedGenCallSummary}
        className="cards"
      >
        <div className="gcallsummary">
          {' '}
          {extractedGenCallSummary
            ? extractedGenCallSummary
            : 'No Call Summary was generated. Please refer to the Call Transcript for additional details.'}
        </div>
      </SummaryCard>
      <ExtractedInfoCard className="cards">
        <header>
          Extracted Information
          {currentUserOUtboundUser && (
            <div className="actions">
              {call.jobType === CallTypeToJobTypeEnum.APIIntegration ? (
                <Button variant="tertiary" size="small" radius="normal" onClick={handleDownloadAPIResponse}>
                  <FaDownload /> UHC API Response
                </Button>
              ) : (
                <>
                  {isCallJobClaimDataAvailable && (
                    <Button variant="tertiary" size="small" radius="normal" onClick={handleDownloadAPIResponse}>
                      <FaDownload /> UHC API Response
                    </Button>
                  )}
                  <Button variant="tertiary" size="small" radius="normal" onClick={handelDownloadExtractor}>
                    <FaDownload /> Extraction Response
                  </Button>
                  {call.useCase === UseCaseEnumToUseCaseIdEnum.ASC && (
                    <Button variant="tertiary" size="small" radius="normal" onClick={handleDownloadAppealExtractor}>
                      <FaDownload /> Extraction HCA Response
                    </Button>
                  )}
                </>
              )}{' '}
            </div>
          )}
        </header>
        <main>
          <div className="call-details-extra">
            <PostCallEntities details={extractIdentification} title="Identification"></PostCallEntities>

            {(call.useCase === UseCaseEnumToUseCaseIdEnum.CSI || call.useCase === UseCaseEnumToUseCaseIdEnum.EOB) && (
              <>
                <PostCallEntities details={extractPayment} title="Payment"></PostCallEntities>
                <PostCallEntities details={extractClaimProcessing} title="Claim Processing"></PostCallEntities>
                <PostCallEntities
                  details={extractPaitentResponsibility}
                  title="Patient Responsibility"
                ></PostCallEntities>
                <PostCallEntities details={extractCharges} title="Charges"></PostCallEntities>
                <PostCallEntities details={extractOtherExtractions} title="Others"></PostCallEntities>
              </>
            )}
            {call.useCase === UseCaseEnumToUseCaseIdEnum.ASC && (
              <>
                <PostCallEntities
                  details={extractAppealStatusInformationSlots}
                  title="Appeal Status Information"
                ></PostCallEntities>
              </>
            )}
            {/* {call.useCase === UseCaseEnumToUseCaseIdEnum.EBV && (
            <>
              <PostCallEntities details={extractDeductibles} title="Deductibles (In-Network) "></PostCallEntities>
              <PostCallEntities details={extractDeductiblesOut} title="Deductibles (Out-of-Network)"></PostCallEntities>
              <PostCallEntities details={extractOutOfPocket} title="Out Of Pocket (In-Network) "></PostCallEntities>
              <PostCallEntities
                details={extractOutOfPocketOut}
                title="Out Of Pocket (Out-of-Network)"
              ></PostCallEntities>
              <PostCallEntities details={extractCoPay} title="Co Pay (In-Network) "></PostCallEntities>
              <PostCallEntities details={extractCoPayOut} title="Co Pay (Out-of-Network)"></PostCallEntities>
              <PostCallEntities details={extractCoInsurance} title="Co Insurance (In-Network) "></PostCallEntities>
              <PostCallEntities
                details={extractCoInsuranceOut}
                title="Co Insurance (Out-of-Network)"
              ></PostCallEntities>
            </>
          )} */}
          </div>
        </main>
      </ExtractedInfoCard>
    </Div>
  )
}

const ExtractedInfoCard = styled.section`
  border-radius: ${({ theme }) => theme.borderRadius.rounded};
  background-color: ${({ theme }) => theme.colors.neutral.lightest};
  border: 1px solid ${({ theme }) => theme.colors.grey.lighter};
  width: 100%;
  padding: 0.1rem;

  > header {
    color: ${({ theme }) => theme.colors.neutral.neutral};
    ${({ theme }) => theme.typography.m.medium};
    padding: ${({ theme }) => `${theme.spacing.xxs} ${theme.spacing.s}`};
    background-color: ${({ theme }) => theme.colors.neutral.lightest};
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey.lighter};
    display: flex;
    justify-content: space-between;
    align-items: center;

    .actions {
      display: flex;
    }
  }

  > main {
    padding: ${({ theme }) => `${theme.spacing.xxs} ${theme.spacing.s}`};
  }
`

const Div = styled.div`
  font-family: ${({ theme }) => theme.fontFamily.milliard};
  display: flex;
  flex-direction: column;

  .provider-wrapper {
    margin-bottom: 1rem;
  }

  .cards {
    margin-bottom: 1rem;

    .gcallsummary {
      ${({ theme }) => theme.typography.m.normal};
      color: ${({ theme }) => theme.colors.neutral.neutral};
    }
  }

  .call-details {
    display: flex;
    align-items: center;
    gap: 1rem 0rem 1rem 3rem;
    flex-wrap: wrap;
    color: ${({ theme }) => theme.colors.primary.darker};
    max-width: 75rem;

    .call-detail {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      flex-grow: 1;

      .call-detail-label {
        font-weight: ${({ theme }) => theme.fontWeight.medium};
        ${({ theme }) => theme.typography.m.normal};
        line-height: ${({ theme }) => theme.spacing.s};
        color: ${({ theme }) => theme.colors.grey.darker};
        span {
          display: inline-flex;
          svg {
            color: ${({ theme }) => theme.colors.grey.light};
            margin-left: 5px;
          }
        }
      }

      .call-detail-value {
        z-index: 1;
      }
    }
  }

  .call-details-extra {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-auto-rows: minmax(min-content, max-content);
    gap: 2rem;
    flex-grow: 1;
    min-height: 40px;
    margin-top: 2rem;
    margin-bottom: 2rem;
    max-width: 80rem;

    .call-details-separator {
      background-color: ${({ theme }) => theme.colors.grey.lighter};
    }

    .call-details-extra-group {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;

      .warning {
        color: #a16d08;
        background: ${({ theme }) => theme.colors.warning.lightest};
        padding: 6px 0px;
        border-radius: ${({ theme }) => theme.borderRadius.normal};
        opacity: 0.8;
        display: grid;
        margin: 10px 0px;
        font-weight: ${({ theme }) => theme.fontWeight.normal};
        ${({ theme }) => theme.typography.xxs.normal};
        line-height: ${({ theme }) => theme.typography.xs};
        text-align: center;
      }

      .title {
        display: flex;
        font-weight: ${({ theme }) => theme.fontWeight.bold};
        ${({ theme }) => theme.typography.xs.normal};
        line-height: 150%;
        color: ${({ theme }) => theme.colors.grey.light};
      }
      .title:after {
        border-bottom: 1px dashed ${({ theme }) => theme.colors.grey.light};
        content: '';
        flex: 1;
        position: relative;
        top: -8px;
        left: -1px;
      }
      .no-data-extracted {
        text-align: center;
        font-weight: ${({ theme }) => theme.fontWeight.medium};
        ${({ theme }) => theme.typography.s.normal};
        line-height: ${({ theme }) => theme.spacing.s};
        text-align: center;
        color: ${({ theme }) => theme.colors.grey.lighter};
      }

      .call-detail-label img {
        float: right;
        margin-left: 5px;
        margin-top: 2px;
      }

      div.click-to-top span {
        display: none;
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        background: ${({ theme }) => theme.colors.grey.lighter};
        color: ${({ theme }) => theme.colors.grey.light};
      }
      .call-detail-label span {
        display: inline-flex;
        svg {
          color: ${({ theme }) => theme.colors.grey.light};
          margin-left: 5px;
          ${({ theme }) => theme.typography.s.normal};
          vertical-align: top;
        }
      }
      .call-detail-value-match {
        background: ${({ theme }) => theme.colors.success.lightest};
        font-weight: ${({ theme }) => theme.fontWeight.normal};
        ${({ theme }) => theme.typography.m.normal};
        float: left;
        line-height: ${({ theme }) => theme.spacing.m};
        text-align: right;
        padding: 5px 10px;
        border-radius: ${({ theme }) => theme.borderRadius.normal};
        svg {
          vertical-align: top;
          color: ${({ theme }) => theme.colors.success.dark};
        }
      }
      .call-detail-value-nomatch {
        background: ${({ theme }) => theme.colors.warning.lightest};
        font-weight: ${({ theme }) => theme.fontWeight.normal};
        ${({ theme }) => theme.typography.m.normal};
        line-height: ${({ theme }) => theme.spacing.m};
        float: left;
        text-align: right;
        padding: 5px 10px;
        border-radius: ${({ theme }) => theme.borderRadius.normal};
        svg {
          vertical-align: top;
          color: ${({ theme }) => theme.colors.warning.dark};
        }
      }
    }

    .call-details-no-extract {
      color: ${({ theme }) => theme.colors.grey.lighter};
      font-weight: ${({ theme }) => theme.fontWeight.medium};
      ${({ theme }) => theme.typography.s.normal};
      line-height: 1rem;
      text-align: center;
      width: 100%;
      max-width: 50%;
      padding: 1.5rem 1rem;
    }

    .call-detail {
      display: flex;
      align-items: center;
      justify-content: space-between;
      position: relative;
      font-weight: ${({ theme }) => theme.fontWeight.medium};
      color: ${({ theme }) => theme.colors.grey.darker};

      &:before {
        content: '';
        position: absolute;
        top: 1rem;
        width: 100%;
        height: 0;
        line-height: 0;
        border-bottom: 2px dotted ${({ theme }) => theme.colors.grey.lighter};
      }

      > * {
        background-color: #fff;
        z-index: 1;
      }

      .call-detail-label {
        font-weight: ${({ theme }) => theme.fontWeight.medium};
        line-height: ${({ theme }) => theme.spacing.s};
        padding-right: 0.5rem;
      }
    }
  }
`
